import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { User, UserLogin } from '../types';
import { apiService } from '../services';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: UserLogin) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  canCreateContent: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        // Verify token is still valid by fetching current user
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
        localStorage.setItem('user', JSON.stringify(currentUser));
      } catch (error) {
        // Token is invalid, clear storage
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (credentials: UserLogin): Promise<boolean> => {
    try {
      setLoading(true);
      const tokenResponse = await apiService.login(credentials);

      // Store token
      localStorage.setItem('access_token', tokenResponse.access_token);

      // Fetch user data
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      return true;
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Login failed';
      console.error('Login error details:', errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Registration removed - admin-only authentication

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const canCreateContent = (): boolean => {
    return !!user; // All authenticated users can create content
  };

  const isAuthenticated = (): boolean => !!user;

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: isAuthenticated(),
    canCreateContent: canCreateContent(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
